import { hero } from "../lib/content";
import { ArrowRightIcon } from "./Icons";
import SmartLink from "./SmartLink";

/**
 * Abstract product preview: shows shapes and relative proportions rather than a
 * fake screenshot. The only real figure on it is `hero.stat`, which is the
 * savings number published on the live site. Swap this for a real screenshot
 * when you have one.
 */
function ProductPreview() {
  const bars = [38, 54, 46, 68, 60, 82, 74, 92, 66, 58, 71, 88];

  return (
    <div className="card overflow-hidden shadow-lift">
      <div className="flex items-center gap-2 border-b border-ink-line bg-ink-soft px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-ink-line" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink-line" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink-line" />
        <span className="ml-3 text-xs font-medium text-ink-muted">
          Cost overview — daily spend by service
        </span>
      </div>

      <div className="grid gap-6 p-6 sm:grid-cols-[1.6fr_1fr]">
        <div>
          <div className="flex items-end justify-between gap-1.5" aria-hidden="true">
            {bars.map((h, i) => (
              <div
                key={i}
                className={`w-full rounded-t-md ${i > 8 ? "bg-brand-500" : "bg-brand-200"}`}
                style={{ height: `${h * 1.5}px` }}
              />
            ))}
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-brand-200" /> Actual
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-brand-500" /> Forecast
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="card border-brand-200 bg-brand-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-700">
              {hero.stat.label}
            </p>
            <p className="mt-1 text-2xl font-extrabold tracking-tight text-brand-700">
              {hero.stat.value}
            </p>
          </div>

          {[
            { label: "Savings opportunities", tone: "bg-accent-success/15 text-accent-success" },
            { label: "Reserved capacity", tone: "bg-brand-100 text-brand-700" },
          ].map((row) => (
            <div key={row.label} className="card p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-ink-muted">{row.label}</span>
                <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${row.tone}`}>
                  <span className="inline-block h-8 w-1.5 rounded-full bg-current align-middle opacity-60" />
                </span>
              </div>
              <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-soft">
                <div className="h-full w-2/3 rounded-full bg-brand-400" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-hero-wash">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-[0.35] [mask-image:radial-gradient(70%_50%_at_50%_0%,black,transparent)]" />

      <div className="container-page relative pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="mx-auto max-w-3xl text-center animate-fade-up">
          <span className="eyebrow">{hero.eyebrow}</span>

          <h1 className="mt-5 text-4xl font-extrabold leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {hero.heading}
          </h1>

          <p className="lead mx-auto max-w-2xl">{hero.subheading}</p>

          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <SmartLink href={hero.primaryCta.href} className="btn-primary w-full sm:w-auto">
              {hero.primaryCta.label}
              <ArrowRightIcon />
            </SmartLink>
            <SmartLink href={hero.secondaryCta.href} className="btn-ghost w-full sm:w-auto">
              {hero.secondaryCta.label}
            </SmartLink>
          </div>

          <p className="mt-6 text-sm font-medium text-ink-muted">{hero.trustLine}</p>
        </div>

        <div className="mx-auto mt-14 max-w-4xl sm:mt-20">
          <ProductPreview />
        </div>
      </div>
    </section>
  );
}
